import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Clock, RefreshCw, Plane, Shield, Smartphone, Zap, MapPin } from "lucide-react";
import { SchemaMarkup, generateBreadcrumbSchema, generateFAQSchema } from "@/components/SchemaMarkup";
import { airports } from "@/data/airports";
import { CURRENT_YEAR } from "@/lib/currentYear";

const RealTimeWaitTimes = () => {
  const topAirports = airports.slice(0, 12);

  const faqData = [
    {
      question: "How often are real-time airport security wait times updated?",
      answer: "Our real-time wait times are updated every 5 minutes using data from TSA and airport systems. This ensures you always see the most current security line conditions."
    },
    {
      question: "What is the difference between real-time and estimated wait times?",
      answer: "Real-time wait times show the actual current wait at security checkpoints right now. Estimated wait times are predictions based on historical data. Real-time data is more accurate for immediate travel decisions."
    },
    {
      question: "Which airports have real-time TSA wait time data?",
      answer: "We provide real-time TSA wait times for 100+ major US airports including ATL, LAX, ORD, DFW, DEN, JFK, SFO, SEA, LAS, and MCO. All major hubs and most regional airports are covered."
    },
    {
      question: "Can I check real-time wait times before leaving home?",
      answer: "Yes! That's the best way to use real-time wait times. Check before you leave home to plan your arrival time and avoid rushing or waiting too long at the airport."
    },
    {
      question: "Are TSA PreCheck wait times shown separately?",
      answer: "Yes, we show both standard security wait times and TSA PreCheck lane wait times separately, so you can see the exact time for your lane type."
    }
  ];

  const faqSchema = generateFAQSchema(faqData);

  return (
    <>
      <Helmet>
        <title>{`TSA Wait Times Right Now – Live Security Lines [Updated ${CURRENT_YEAR}]`}</title>
        <meta name="description" content="Check TSA wait times RIGHT NOW at 100+ airports. ⏱️ Live airport security data updated every 5 min. See current wait before you leave home." />
        <meta name="keywords" content="TSA wait time right now, airport security wait time today, live TSA wait times, current security line wait, airport wait times now, real-time TSA data" />
        <link rel="canonical" href="https://airportbuddy.app/real-time-wait-times" />
        
        {/* Freshness signals */}
        <meta property="article:modified_time" content="2026-01-30" />
        <meta property="og:updated_time" content="2026-01-30" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://airportbuddy.app/real-time-wait-times" />
        <meta property="og:title" content="TSA Wait Times Right Now – Live at 100+ Airports" />
        <meta property="og:description" content="Check live airport security wait times for 100+ airports. Updated every 5 minutes." />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`TSA Wait Times Right Now [${CURRENT_YEAR}]`} />
        <meta name="twitter:description" content="Live TSA wait times for 100+ airports. Check before you leave." />
      </Helmet>

      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Real-Time Wait Times", url: "/real-time-wait-times" }
      ])} />
      <SchemaMarkup schema={faqSchema} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </nav>

          {/* Hero Section */}
          <header className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-500/10 text-green-600 dark:text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <RefreshCw className="h-4 w-4 animate-spin" style={{ animationDuration: '3s' }} />
              Updated Every 5 Minutes
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Real-Time Airport Security Wait Times
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              See live TSA checkpoint wait times for 100+ US airports. Check before you leave home to arrive at the perfect time.
            </p>
          </header>

          {/* Key Benefits */}
          <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
            {[
              { icon: Zap, title: "Live Data", desc: "Updated every 5 min" },
              { icon: MapPin, title: "100+ Airports", desc: "All major US hubs" },
              { icon: Shield, title: "PreCheck Times", desc: "Separate lane data" },
              { icon: Smartphone, title: "Mobile Ready", desc: "Check anywhere" }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="text-center p-4">
                  <div className="bg-primary/10 p-3 rounded-full w-fit mx-auto mb-2">
                    <IconComponent className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm">{item.title}</h3>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </Card>
              );
            })}
          </div>

          {/* Airport Grid */}
          <section className="max-w-5xl mx-auto mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
              Check Real-Time Wait Times by Airport
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {topAirports.map((airport) => (
                <Link 
                  key={airport.code} 
                  to={`/airports/${airport.code.toLowerCase()}`}
                  className="block"
                >
                  <Card className="p-4 hover:shadow-lg hover:border-primary transition-all">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Plane className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-bold text-foreground">{airport.code}</p>
                        <p className="text-xs text-muted-foreground">{airport.city}</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-6">
              <Button asChild>
                <Link to="/auth">View All 100+ Airports</Link>
              </Button>
            </div>
          </section>

          {/* How It Works */}
          <section className="max-w-4xl mx-auto mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
              How Real-Time Wait Times Work
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6">
                <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">1</div>
                <h3 className="font-semibold text-foreground mb-2">Select Your Airport</h3>
                <p className="text-sm text-muted-foreground">Choose from 100+ US airports including all major hubs.</p>
              </Card>
              <Card className="p-6">
                <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">2</div>
                <h3 className="font-semibold text-foreground mb-2">View Live Wait Times</h3>
                <p className="text-sm text-muted-foreground">See current TSA wait times for standard and PreCheck lanes.</p>
              </Card>
              <Card className="p-6">
                <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">3</div>
                <h3 className="font-semibold text-foreground mb-2">Plan Your Arrival</h3>
                <p className="text-sm text-muted-foreground">Leave at the right time to avoid stress and long waits.</p>
              </Card>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-5">
                  <h3 className="font-semibold text-foreground mb-2">{faq.question}</h3>
                  <p className="text-sm text-muted-foreground">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <div className="max-w-4xl mx-auto">
            <Card className="bg-primary text-primary-foreground p-8 text-center">
              <Clock className="h-12 w-12 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-3">Check Wait Times Right Now</h2>
              <p className="text-primary-foreground/80 mb-6 max-w-xl mx-auto">
                Don't guess how early to leave. See real-time TSA wait times and arrive at the perfect time for your flight.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg" variant="secondary">
                  <Link to="/">Check Current Wait Times</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                  <Link to="/tips/minimize-wait-times">See Time-Saving Tips</Link>
                </Button>
              </div>
            </Card>
          </div>

          {/* Related Links */}
          <div className="max-w-4xl mx-auto mt-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/tips/minimize-wait-times" className="block">
                <Card className="p-4 hover:shadow-md hover:border-primary transition-all">
                  <p className="font-semibold text-foreground">Top 5 Tips to Minimize Wait Times</p>
                  <p className="text-sm text-muted-foreground">Save 30+ minutes at security</p>
                </Card>
              </Link>
              <Link to="/blog/tips-for-avoiding-long-lines-at-airport-security" className="block">
                <Card className="p-4 hover:shadow-md hover:border-primary transition-all">
                  <p className="font-semibold text-foreground">Avoid Long Security Lines</p>
                  <p className="text-sm text-muted-foreground">15 proven strategies</p>
                </Card>
              </Link>
              <Link to="/blog/tsa-precheck-vs-clear-which-is-better" className="block">
                <Card className="p-4 hover:shadow-md hover:border-primary transition-all">
                  <p className="font-semibold text-foreground">TSA PreCheck vs CLEAR</p>
                  <p className="text-sm text-muted-foreground">Which is worth it?</p>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RealTimeWaitTimes;
